import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Typography, TextField, Skeleton, Switch, CircularProgress } from "@mui/material";
import CloudIcon from "@mui/icons-material/Cloud";
import SaveIcon from "@mui/icons-material/Save";

import useClickHouse from "../../../hooks/super/useClickHouse";
import useAuth from "../../../hooks/useAuth";

import EndpointHistoryList from "./component/T_EndpointHistory";
import AuthInfoHistoryList from "./component/T_AuthInfoHistory";
import { CollapseAlert } from "../../../components/pages/common/styled";
import { UserType } from "../../../utils/constants";
import { formatDate } from "../../../utils/format";

import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import {
  TOKENS,
  SectionCard,
} from "../../../components/pages/_new/super/design";

function SAClickHouse() {
  const {
    getCurrentEndpoint,
    getEndpointHistory,
    updateEndpoint,
    getCurrentAuthInfo,
    getAuthInfoHistory,
    updateAuthInfo,
    getCloudConfig,
    updateCloudConfig,
    endpointSize,
    authInfoSize,
    currentEndpoint,
    currentAuthInfo,
    cloudEnabled,
    cloudConfigLoaded,
    errMsg,
    setErr,
  } = useClickHouse();
  const { isAuthenticated, adminUserType } = useAuth();
  const [cloudToggling, setCloudToggling] = React.useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      getCurrentEndpoint();
      getEndpointHistory(endpointSize, 0);
      getCurrentAuthInfo();
      getAuthInfoHistory(authInfoSize, 0);
      getCloudConfig();
    }
    return () => setErr(null);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  const handleCloudToggle = async () => {
    setCloudToggling(true);
    const success = await updateCloudConfig(!cloudEnabled);
    if (!success) await getCloudConfig();
    setCloudToggling(false);
  };

  const endpointRef = React.useRef(null);
  const usernameRef = React.useRef(null);
  const passwordRef = React.useRef(null);
  const [endpointValue, setEndpointValue] = React.useState("");
  const [usernameValue, setUsernameValue] = React.useState("");
  const [passwordValue, setPasswordValue] = React.useState("");

  const changeEndpoint = (e) => setEndpointValue(e.target.value);
  const changeUsername = (e) => setUsernameValue(e.target.value);
  const changePassword = (e) => setPasswordValue(e.target.value);

  const saveEndpoint = () => {
    if (!endpointValue) {
      endpointRef.current?.focus();
      return;
    }
    updateEndpoint(endpointValue);
    setEndpointValue("");
  };

  const saveAuthInfo = () => {
    if (!usernameValue) {
      usernameRef.current?.focus();
      return;
    }
    if (!passwordValue) {
      passwordRef.current?.focus();
      return;
    }
    updateAuthInfo(usernameValue, passwordValue);
    setUsernameValue("");
    setPasswordValue("");
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const isSuper = UserType.SUPER_ADMIN === adminUserType;

  return (
    <React.Fragment>
      <Helmet title="SA ClickHouse Management" />
      <PageHeader title="ClickHouse Management" description="Configure ClickHouse endpoint and authentication used by the WAF engines" />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Stack spacing={2}>
        <SectionCard
          title="Cloud mode"
          description={
            cloudEnabled
              ? "Cloud mode enabled — WAF engines use bearer token authentication"
              : "Self-hosted mode — WAF engines use basic auth (username/password)"
          }
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {cloudToggling && <CircularProgress size={18} sx={{ color: TOKENS.primary }} />}
              {!cloudConfigLoaded ? (
                <Skeleton width={50} height={30} variant="rectangular" sx={{ borderRadius: `${TOKENS.radiusXs}px` }} />
              ) : (
                <Switch
                  checked={cloudEnabled}
                  onChange={handleCloudToggle}
                  disabled={cloudToggling || !isSuper}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: TOKENS.primary },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: TOKENS.primary },
                  }}
                />
              )}
            </Box>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: `${TOKENS.radiusXs}px`,
                backgroundColor: cloudEnabled ? TOKENS.primaryTint : TOKENS.bgSunken,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloudIcon sx={{ color: cloudEnabled ? TOKENS.primary : TOKENS.fg3 }} />
            </Box>
            <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>
              {cloudEnabled ? "Bearer token authentication active" : "Basic auth active"}
            </Typography>
          </Box>
        </SectionCard>

        <SectionCard
          title="Endpoint"
          description={
            currentEndpoint?.endpoint
              ? `Current: ${currentEndpoint.endpoint}${
                  currentEndpoint?.updated ? ` (updated ${formatDate(currentEndpoint.updated)})` : ""
                }`
              : "No endpoint configured"
          }
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ maxWidth: 720, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="https://clickhouse.example.com:8443"
              required
              value={endpointValue}
              onChange={changeEndpoint}
              inputRef={endpointRef}
              sx={inputSx}
            />
            <PrimaryButton
              onClick={saveEndpoint}
              startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
              disabled={!isSuper}
            >
              Save
            </PrimaryButton>
          </Stack>
          <EndpointHistoryList />
        </SectionCard>

        <SectionCard
          title="Authentication"
          description={
            currentAuthInfo?.username
              ? `Current username: ${currentAuthInfo.username}${
                  currentAuthInfo?.updated ? ` (updated ${formatDate(currentAuthInfo.updated)})` : ""
                }`
              : "No credentials configured"
          }
        >
          <Stack spacing={1.5} sx={{ maxWidth: 720, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Username"
              required
              value={usernameValue}
              onChange={changeUsername}
              inputRef={usernameRef}
              sx={inputSx}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="Password"
              type="password"
              required
              value={passwordValue}
              onChange={changePassword}
              inputRef={passwordRef}
              sx={inputSx}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PrimaryButton
                onClick={saveAuthInfo}
                startIcon={<SaveIcon style={{ width: 18, height: 18 }} />}
                disabled={!isSuper}
              >
                Save
              </PrimaryButton>
            </Box>
          </Stack>
          <AuthInfoHistoryList />
        </SectionCard>
      </Stack>
    </React.Fragment>
  );
}

export default SAClickHouse;
